import type { RouteRecordRaw } from 'vue-router';

// inbox-101 — публичные routes для prerender'a через vite-ssg.
// vite-ssg сам пройдёт по этому списку и сгенерит статический HTML
// для каждой страницы (см. vite.config.ts -> ssgOptions.includedRoutes).
//
// Cabinet/auth-routes добавятся в inbox-96/97/98 — они НЕ prerender'ятся
// (защищены auth-cookie, не нужны в индексе поисковика).

export const routes: RouteRecordRaw[] = [
  {
    path: '/',
    name: 'home',
    component: () => import('@/views/public/HomeView.vue'),
    meta: { prerender: true, layout: 'public' },
  },
  {
    path: '/features',
    name: 'features',
    component: () => import('@/views/public/FeaturesView.vue'),
    meta: { prerender: true, layout: 'public' },
  },
  {
    path: '/pricing',
    name: 'pricing',
    component: () => import('@/views/public/PricingView.vue'),
    meta: { prerender: true, layout: 'public' },
  },
  {
    path: '/ai',
    name: 'ai',
    component: () => import('@/views/public/AiView.vue'),
    meta: { prerender: true, layout: 'public' },
  },
  {
    path: '/integrations',
    name: 'integrations',
    component: () => import('@/views/public/IntegrationsView.vue'),
    meta: { prerender: true, layout: 'public' },
  },
  {
    path: '/cases',
    name: 'cases',
    component: () => import('@/views/public/CasesView.vue'),
    meta: { prerender: true, layout: 'public' },
  },
  {
    path: '/docs',
    name: 'docs',
    component: () => import('@/views/public/DocsView.vue'),
    meta: { prerender: true, layout: 'public' },
  },
  {
    path: '/about',
    name: 'about',
    component: () => import('@/views/public/AboutView.vue'),
    meta: { prerender: true, layout: 'public' },
  },
  // inbox-337 — compliance + trust pages (legal текст для платных тарифов).
  {
    path: '/contact',
    name: 'contact',
    component: () => import('@/views/public/ContactView.vue'),
    meta: { prerender: true, layout: 'public' },
  },
  {
    path: '/privacy',
    name: 'privacy',
    component: () => import('@/views/public/PrivacyView.vue'),
    meta: { prerender: true, layout: 'public' },
  },
  {
    path: '/terms',
    name: 'terms',
    component: () => import('@/views/public/TermsView.vue'),
    meta: { prerender: true, layout: 'public' },
  },
  // ── Cabinet routes (NOT prerender'ятся — auth-cookie protected) ──
  // AppCabinet layout встанет в G2 (inbox-96); пока views render'ятся
  // standalone, под своей минимальной обёрткой.
  // inbox-133 — settings зона: parent SettingsLayout с 220px sub-sidebar +
  // 8 children через nested RouterView. Заменяет flat-routes inbox-105.
  {
    path: '/settings',
    component: () => import('@/layouts/SettingsLayout.vue'),
    meta: { prerender: false, layout: 'cabinet', requiresAuth: true },
    children: [
      { path: '', redirect: '/settings/profile' },
      {
        path: 'profile',
        name: 'settings-profile',
        component: () => import('@/views/settings/SettingsProfileView.vue'),
      },
      {
        path: 'team',
        name: 'settings-team',
        component: () => import('@/views/settings/SettingsTeamView.vue'),
      },
      {
        path: 'org',
        name: 'settings-org',
        component: () => import('@/views/settings/SettingsOrgView.vue'),
      },
      {
        path: 'billing',
        name: 'settings-billing',
        component: () => import('@/views/settings/SettingsBillingView.vue'),
      },
      {
        path: 'domains',
        name: 'settings-domains',
        component: () => import('@/views/settings/SettingsDomainsView.vue'),
      },
      {
        // inbox-296 — alias 'api-keys' для MCP smoke и UX-консистентности
        // с названием view (SettingsApiKeysView). Оба URL валидны.
        path: 'api',
        alias: 'api-keys',
        name: 'settings-api',
        component: () => import('@/views/settings/SettingsApiKeysView.vue'),
      },
      {
        path: 'webhooks',
        name: 'settings-webhooks',
        component: () => import('@/views/settings/SettingsWebhooksView.vue'),
      },
      {
        path: 'audit',
        name: 'settings-audit',
        component: () => import('@/views/settings/SettingsAuditView.vue'),
      },
      {
        // inbox-296 — placeholder для /settings/notifications (раньше
        // отдавал пустой SPA-shell). UiDevCard до /api/v1/users/me/notification-preferences.
        path: 'notifications',
        name: 'settings-notifications',
        component: () => import('@/views/settings/SettingsNotificationsView.vue'),
      },
      {
        // inbox-296 — placeholder для /settings/security (2FA / sessions
        // / passkey ещё не в UI).
        path: 'security',
        name: 'settings-security',
        component: () => import('@/views/settings/SettingsSecurityView.vue'),
      },
    ],
  },
  // ── Console (staff platform) (inbox-268) — все child routes под /console.
  // meta.requiresStaff проверяется router.beforeEach guard'ом (см.
  // setupShared в main.ts). inbox-269 наполнит views реальным содержанием.
  {
    path: '/console',
    component: () => import('@/layouts/ConsoleLayout.vue'),
    meta: { prerender: false, layout: 'console', requiresStaff: true },
    children: [
      // inbox-269 — каждый view получает dedicated component с реальным fetch.
      { path: '', name: 'console-dashboard', component: () => import('@/views/console/ConsoleDashboardView.vue') },
      { path: 'users', name: 'console-users', component: () => import('@/views/console/ConsoleUsersView.vue') },
      { path: 'users/:id', name: 'console-user-detail', component: () => import('@/views/console/ConsoleUserDetailView.vue') },
      { path: 'orgs', name: 'console-orgs', component: () => import('@/views/console/ConsoleOrgsView.vue') },
      { path: 'orgs/:id', name: 'console-org-detail', component: () => import('@/views/console/ConsoleOrgDetailView.vue') },
      { path: 'audit', name: 'console-audit', component: () => import('@/views/console/ConsoleAuditView.vue') },
      { path: 'finance', name: 'console-finance', component: () => import('@/views/console/ConsoleFinanceView.vue') },
      { path: 'feature-flags', name: 'console-feature-flags', component: () => import('@/views/console/ConsoleFeatureFlagsView.vue') },
      { path: 'system', name: 'console-system', component: () => import('@/views/console/ConsoleSystemView.vue') },
    ],
  },
  // ── Public share-token report (inbox-243) — без auth, layout=public.
  // Backend GET /api/v1/share/campaigns/{token} pending; на 404/disabled
  // показывает «Публичная ссылка недоступна» state.
  {
    path: '/share/campaigns/:token',
    name: 'public-campaign-report',
    component: () => import('@/views/public/PublicCampaignReportView.vue'),
    meta: { prerender: false, layout: 'public' },
  },
  // ── Auth flow ── (inbox-91 — onboarding wizard, F1 roadmap)
  {
    path: '/onboarding',
    name: 'onboarding',
    component: () => import('@/views/auth/OnboardingView.vue'),
    meta: { prerender: false, layout: 'auth', requiresAuth: true },
  },
  // ── Bio (inbox-89 — list + constructor, E3 roadmap) ──
  // inbox-485 — публичный рендер /@:slug. Маршрут работает только на
  // коротких доменах (hopw.cc) — Caddy на основном link.helmix.app
  // отправляет /@* в backend catch-all (намеренно).
  {
    path: '/@:slug',
    name: 'bio-public',
    component: () => import('@/views/bio/BioPublicView.vue'),
    meta: { prerender: false, layout: 'public' },
  },
  {
    path: '/bio',
    name: 'bio-list',
    component: () => import('@/views/bio/BioListView.vue'),
    meta: { prerender: false, layout: 'cabinet', requiresAuth: true },
  },
  {
    path: '/bio/:id/edit',
    name: 'bio-constructor',
    component: () => import('@/views/bio/BioConstructorView.vue'),
    meta: { prerender: false, layout: 'cabinet', requiresAuth: true },
  },
  // ── Campaigns (inbox-100 — list + detail, G6 roadmap) ──
  // inbox-434 regression-guard: /campaigns должен резолвить
  // CampaignsListView напрямую (НЕ redirect на /settings/profile или
  // куда-либо ещё, ни на desktop, ни на mobile). MCP walk 22.05 пометил
  // подозрение на mobile-redirect — статический аудит чистый, защита
  // через e2e spec web/tests/e2e/mobile-campaigns-no-redirect.spec.ts.
  {
    path: '/campaigns',
    name: 'campaigns-list',
    component: () => import('@/views/campaigns/CampaignsListView.vue'),
    meta: { prerender: false, layout: 'cabinet', requiresAuth: true },
  },
  {
    path: '/campaigns/:id',
    name: 'campaign-detail',
    component: () => import('@/views/campaigns/CampaignDetailView.vue'),
    meta: { prerender: false, layout: 'cabinet', requiresAuth: true },
  },
  // ── QR codes (inbox-100 — list + customizer, G6 roadmap) ──
  {
    path: '/qr',
    name: 'qr-codes',
    component: () => import('@/views/qr/QrCodesView.vue'),
    meta: { prerender: false, layout: 'cabinet', requiresAuth: true },
  },
  // ── Dashboard + Links (inbox-103 + inbox-104) ──
  {
    path: '/dashboard',
    name: 'dashboard',
    component: () => import('@/views/cabinet/DashboardView.vue'),
    meta: { prerender: false, layout: 'cabinet', requiresAuth: true },
  },
  {
    path: '/links',
    name: 'links-list',
    component: () => import('@/views/cabinet/LinksListView.vue'),
    meta: { prerender: false, layout: 'cabinet', requiresAuth: true },
  },
  {
    // inbox-249 — детальная аналитика ссылки (4 tabs: Обзор / Источники / География / Аудитория).
    path: '/links/:id(\\d+)',
    name: 'link-detail',
    component: () => import('@/views/cabinet/LinkDetailView.vue'),
    meta: { prerender: false, layout: 'cabinet', requiresAuth: true },
  },
  // ── inbox-168 + 174 + 175: cabinet sidebar routes (full ports + placeholders).
  {
    // inbox-174 — analytics dashboards (1:1 port).
    // inbox-296 — alias /analytics-dashboards для MCP smoke и URL,
    // совпадающего с именем view (AnalyticsDashboardsView).
    path: '/analytics',
    alias: '/analytics-dashboards',
    name: 'analytics',
    component: () => import('@/views/cabinet/AnalyticsDashboardsView.vue'),
    meta: { prerender: false, layout: 'cabinet', requiresAuth: true },
  },
  {
    // inbox-151 — antifraud report (placeholder с UiDevCard).
    path: '/antifraud',
    name: 'antifraud',
    component: () => import('@/views/cabinet/AntifraudReportView.vue'),
    meta: { prerender: false, layout: 'cabinet', requiresAuth: true },
  },
  {
    // inbox-175 — AI copilot (1:1 port, backend pending).
    // inbox-296 — alias /ai-copilot (совпадает с именем view AiCopilotView).
    path: '/copilot',
    alias: '/ai-copilot',
    name: 'copilot',
    component: () => import('@/views/cabinet/AiCopilotView.vue'),
    meta: { prerender: false, layout: 'cabinet', requiresAuth: true },
  },
  {
    // inbox-479 — cabinet «Интеграции» (управление подключениями).
    // Отдельно от public /integrations (маркетинг каталог).
    path: '/connections',
    name: 'connections',
    component: () => import('@/views/cabinet/ConnectionsView.vue'),
    meta: { prerender: false, layout: 'cabinet', requiresAuth: true },
  },
  {
    // inbox-168 — conversions placeholder.
    path: '/conversions',
    name: 'conversions',
    component: () => import('@/views/cabinet/ConversionsView.vue'),
    meta: { prerender: false, layout: 'cabinet', requiresAuth: true },
  },
  {
    // inbox-283 — full-screen mobile menu, открывается из BottomNav.
    path: '/menu',
    name: 'menu',
    component: () => import('@/views/cabinet/MenuView.vue'),
    meta: { prerender: false, layout: 'cabinet', requiresAuth: true },
  },
  // ── Auth views — real components (replaces redirect-aliases).
  {
    path: '/login',
    name: 'login',
    component: () => import('@/views/auth/LoginView.vue'),
    meta: { prerender: false, layout: 'auth' },
  },
  {
    path: '/signup',
    name: 'signup',
    component: () => import('@/views/auth/SignupView.vue'),
    meta: { prerender: false, layout: 'auth' },
  },
  {
    path: '/recover',
    name: 'recover',
    component: () => import('@/views/auth/RecoverView.vue'),
    meta: { prerender: false, layout: 'auth' },
  },
  {
    path: '/confirm',
    name: 'confirm',
    component: () => import('@/views/auth/ConfirmView.vue'),
    meta: { prerender: false, layout: 'auth' },
  },
  { path: '/verify', redirect: '/onboarding' },
  // ── /app/* prefix alias (inbox-112 prod fix) — Caddyfile @spa matcher
  // отдаёт spa-shell.html для /app/* URL'ов, но Vue Router routes flat
  // (/dashboard, /links, /settings/* и т.д.). Redirect /app/<rest> →
  // /<rest>, чтобы deep-link'и /app/dashboard работали.
  // Используем vue-router 4 catch-all синтаксис `:pathMatch(.*)*`.
  {
    path: '/app/:pathMatch(.*)*',
    redirect: (to) => {
      const pm = to.params.pathMatch;
      const tail = Array.isArray(pm) ? pm.join('/') : (pm as string) || '';
      return `/${tail || 'dashboard'}`;
    },
  },
  { path: '/app', redirect: '/dashboard' },
  // inbox-456 — явные маршруты /error/404 и /error/500 для использования
  // глобальным error-handler'ом (api-client при 5xx → push('/error/500')).
  {
    path: '/error/404',
    name: 'error-404',
    component: () => import('@/views/public/NotFoundView.vue'),
    meta: { prerender: false, layout: 'public' },
  },
  {
    path: '/error/500',
    name: 'error-500',
    component: () => import('@/views/public/InternalErrorView.vue'),
    meta: { prerender: false, layout: 'public' },
  },
  // inbox-296/inbox-456 — catch-all для неизвестных путей. До inbox-296
  // vue-router тихо рендерил пустой <RouterView />. До inbox-456 catch-all
  // указывал на cabinet/NotFoundView (требовал auth). Теперь публичный
  // NotFoundView — работает и для гостей и для авторизованных.
  // ВАЖНО: backend короткие ссылки (/<slug>) обрабатываются Caddy/FastAPI
  // ДО клиентского router'а, так что эта route не перехватит /<slug>.
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: () => import('@/views/public/NotFoundView.vue'),
    meta: { prerender: false, layout: 'public' },
  },
];

// Список путей, по которым sitemap.xml будет сгенерён в postbuild step.
// Держим отдельно, чтобы скрипт мог распарсить без vue-runtime'а.
export const PRERENDER_PATHS = [
  '/',
  '/features',
  '/pricing',
  '/ai',
  '/integrations',
  '/cases',
  '/docs',
  '/about',
  // inbox-337 — compliance pages for sitemap.xml + Yandex indexing.
  '/contact',
  '/privacy',
  '/terms',
] as const;
